import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
    useHandwritingProjectService,
    type HandwritingProjectCreateResult,
} from '~~/layers/base/services/handwritingProject.service'
import type { HandwritingProjectRecord } from '~~/layers/base/types/handwritingProject'
import { isStartPageWorkCancelled } from '~~/layers/base/utils/fetchAbort'

function isFullHandwritingProjectRecord(
    value: HandwritingProjectCreateResult | null
): value is HandwritingProjectRecord {
    return value !== null && typeof value === 'object' && 'profile_id' in value
}

export const useHandwritingProjectStore = defineStore('handwritingProject', () => {
    const { doCreateHandwritingProject, doFetchHandwritingProject } = useHandwritingProjectService()

    const handwritingProjectId = ref<string | null>(null)
    const handwritingProjectDetails = ref<HandwritingProjectRecord | null>(null)
    const handwritingProjectDetailsPending = ref(false)
    const handwritingProjectDetailsError = ref<unknown | null>(null)

    type CreateHandwritingProjectArgs = {
        /** Optional recipient foreign key. */
        contactId?: string | null
        /** Optional recipient address foreign key. */
        recipientAddressId?: string | null
        /** Optional return address foreign key. */
        returnAddressId?: string | null
        /** When set, aborts the POST if the start page route is left. */
        signal?: AbortSignal
    }

    function pushHandwritingProject(project: HandwritingProjectRecord): void {
        handwritingProjectDetails.value = project
        handwritingProjectId.value = project.id
        handwritingProjectDetailsError.value = null
    }

    /**
     * Loads the project when missing, id mismatches, or `force` is true. Skips GET when the
     * store already holds the same id and `force` is false.
     */
    async function ensureHandwritingProject(
        id: string,
        options?: { force?: boolean }
    ): Promise<HandwritingProjectRecord | null> {
        const trimmed = id.trim()
        if (!trimmed) {
            return null
        }

        const force = options?.force === true
        const cached = handwritingProjectDetails.value
        const needsFetch = force || !cached || cached.id !== trimmed

        if (!needsFetch) {
            return cached
        }

        handwritingProjectDetailsPending.value = true
        handwritingProjectDetailsError.value = null
        try {
            const { data, error } = await doFetchHandwritingProject(trimmed)
            if (error.value) {
                handwritingProjectDetailsError.value = error.value
                handwritingProjectDetails.value = null
                return null
            }
            const record = data.value ?? null
            if (record) {
                pushHandwritingProject(record)
            } else {
                handwritingProjectDetails.value = null
                handwritingProjectId.value = trimmed
            }
            return record
        } finally {
            handwritingProjectDetailsPending.value = false
        }
    }

    const createHandwritingProject = async (
        args: CreateHandwritingProjectArgs = {}
    ): Promise<HandwritingProjectCreateResult | null> => {
        const { signal, contactId, recipientAddressId, returnAddressId } = args
        const { data, error } = await doCreateHandwritingProject(
            {
                contact_id: contactId ?? null,
                recipient_address_id: recipientAddressId ?? null,

                // TODO: add default return address from profile
                return_address_id: returnAddressId ?? null,
            },
            { signal }
        )
        if (error.value) {
            if (signal && isStartPageWorkCancelled(signal, error.value)) {
                return null
            }
            throw error.value
        }

        const created = data.value ?? null
        handwritingProjectId.value = created?.id ?? null
        if (isFullHandwritingProjectRecord(created)) {
            pushHandwritingProject(created)
        }
        return created
    }

    /** Drop cached project id/details (e.g. logout session wipe). */
    function clearHandwritingProject(): void {
        handwritingProjectId.value = null
        handwritingProjectDetails.value = null
        handwritingProjectDetailsPending.value = false
        handwritingProjectDetailsError.value = null
    }

    return {
        handwritingProjectId,
        handwritingProjectDetails,
        handwritingProjectDetailsPending,
        handwritingProjectDetailsError,
        pushHandwritingProject,
        ensureHandwritingProject,
        createHandwritingProject,
        clearHandwritingProject,
    }
})
